import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Upload, 
  Users, 
  Download,
  Trash2,
  Search,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const API = `${API_BASE}/api`;

export default function ProgrammeLearnersPage() {
  const navigate = useNavigate();
  const { programmeId } = useParams();
  const { token } = useAuth();
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const [programme, setProgramme] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, [programmeId]);

  const fetchData = async () => {
    try {
      const [progRes, enrollRes] = await Promise.all([
        axios.get(`${API}/programmes/${programmeId}`, { headers }),
        axios.get(`${API}/programmes/${programmeId}/enrollments`, { headers })
      ]);
      setProgramme(progRes.data);
      setEnrollments(enrollRes.data.enrollments || []);
    } catch (error) {
      toast.error('Failed to load data');
      navigate('/programmes');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(
        `${API}/programmes/${programmeId}/enroll-learners`,
        formData,
        { 
          headers: { 
            ...headers, 
            'Content-Type': 'multipart/form-data' 
          } 
        }
      );

      toast.success(`Enrolled ${res.data.enrolled_count} learners`);
      
      if (res.data.errors && res.data.errors.length > 0) {
        console.warn('Enrollment errors:', res.data.errors);
        toast.warning(`${res.data.errors.length} rows had errors`);
      }

      fetchData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload learners');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveEnrollment = async (enrollmentId, email) => {
    if (!window.confirm(`Remove ${email} from this programme?`)) return;

    try {
      await axios.delete(`${API}/programmes/${programmeId}/enrollments/${enrollmentId}`, { headers });
      toast.success('Learner removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove learner');
    }
  };

  const downloadTemplate = () => {
    const csvContent = "email,first_name,last_name,employee_id,department\njohn@example.com,John,Doe,EMP001,Compliance\njane@example.com,Jane,Smith,EMP002,Risk";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'learner_enrollment_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredEnrollments = enrollments.filter(e => 
    e.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.last_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-zinc-800 rounded w-1/3" />
            <div className="h-64 bg-zinc-800 rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(`/admin/programmes/${programmeId}/edit`)}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Programme
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">
                Manage Learners
              </h1>
              <p className="text-sm text-zinc-400">
                {programme?.programme_name || 'Programme'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={downloadTemplate}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".csv,.xlsx,.xls"
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Users className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{enrollments.length}</p>
                  <p className="text-sm text-zinc-400">Total Enrolled</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-teal-500/20">
                  <CheckCircle className="h-5 w-5 text-teal-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {enrollments.filter(e => e.overall_progress === 100).length}
                  </p>
                  <p className="text-sm text-zinc-400">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <FileSpreadsheet className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {enrollments.filter(e => e.overall_progress > 0 && e.overall_progress < 100).length}
                  </p>
                  <p className="text-sm text-zinc-400">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <AlertCircle className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {enrollments.filter(e => e.overall_progress === 0).length}
                  </p>
                  <p className="text-sm text-zinc-400">Not Started</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              placeholder="Search learners..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-zinc-900 border-zinc-800 text-white"
            />
          </div>
        </div>

        {/* Enrollments Table */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="h-5 w-5" />
              Enrolled Learners ({filteredEnrollments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {enrollments.length === 0 ? (
              <div className="text-center py-12">
                <FileSpreadsheet className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No learners enrolled</h3>
                <p className="text-zinc-400 mb-4">
                  Upload a CSV file to enroll learners in this programme
                </p>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload CSV
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-400">Name</TableHead>
                    <TableHead className="text-zinc-400">Email</TableHead>
                    <TableHead className="text-zinc-400">Department</TableHead>
                    <TableHead className="text-zinc-400">Progress</TableHead>
                    <TableHead className="text-zinc-400">Grade</TableHead>
                    <TableHead className="text-zinc-400">Status</TableHead>
                    <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEnrollments.map((enrollment) => (
                    <TableRow key={enrollment.enrollment_id} className="border-zinc-800">
                      <TableCell className="text-white font-medium">
                        {enrollment.first_name} {enrollment.last_name}
                      </TableCell>
                      <TableCell className="text-zinc-400">{enrollment.email}</TableCell>
                      <TableCell className="text-zinc-400">{enrollment.department || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-zinc-800 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-teal-500 rounded-full"
                              style={{ width: `${enrollment.overall_progress || 0}%` }}
                            />
                          </div>
                          <span className="text-sm text-zinc-400">
                            {enrollment.overall_progress || 0}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-white">
                        {enrollment.overall_grade !== null ? `${enrollment.overall_grade}%` : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={enrollment.status === 'active' ? 'default' : 'secondary'}
                          className={enrollment.status === 'active' ? 'bg-teal-500/20 text-teal-400' : ''}
                        >
                          {enrollment.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveEnrollment(enrollment.enrollment_id, enrollment.email)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
